import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'
import {createContext, use} from 'react'

import type {MergeBoxRollupStatus} from '../../../../helpers/merge-box-status-calculator/types'
import type {
  AutoMergeableStackEntry,
  StackSectionStackEntry,
} from '../../../../helpers/merge-box-status-calculator/stack-section-types'
import type {Status} from '../../../../helpers/mergeability-status'
import type {
  JSONAPIPullRequestPayload,
  PullRequestMergeRequirementsPayload,
} from '../../../../page-data/payloads/merge-box'
import type {PullRequestMergeAction} from '../../../../types'
import type {DirectMergeActionsSectionProps, MergeButtonFocusProps} from '../DirectMergeActionsSection'
import type {MergeControlModel} from './derive-merge-control-model'

export type MergeControlProps = {
  baseRefName: string
  canUserPushToBase: boolean
  conflictsCondition: DirectMergeActionsSectionProps['conflictsCondition']
  handleConfirmingMergeInfo: (isConfirming: boolean) => void
  mergeCurrentlyBlockedForStacks?: boolean
  helpUrl: string
  isConfirmingMergeInfo: boolean
  isStackMergeInProgress?: boolean
  isStacked?: boolean
  stackedEntriesToMergeCount?: number
  stackedEntries?: StackSectionStackEntry[]
  entriesThatCanBeAutoMerged?: AutoMergeableStackEntry[]
  stackMetadata?: PullRequestUserActionStack
  mergeBoxRollupStatus: MergeBoxRollupStatus
  mergeRequirements: PullRequestMergeRequirementsPayload
  onStackMergePendingChange?: (pendingAction?: PullRequestMergeAction) => void
  status: Status
  pullRequest: Omit<JSONAPIPullRequestPayload, 'baseRefName'>
} & MergeButtonFocusProps

export type MergeControlState = {
  isAdminBypassToggleChecked: boolean
  isBypassAllowed: boolean
  isConfirmingMergeInfo: boolean
  isMergeable: boolean
  isMergeQueueEnabled: boolean
  mergeControlModel: MergeControlModel
  currentStackEntryPositionNumber?: number
  stackSize?: number
}

export const MergeControlStateContext = createContext<MergeControlState | null>(null)
export const MergeControlActionsContext = createContext<(() => void) | null>(null)
export const MergeControlInputsContext = createContext<MergeControlProps | null>(null)

export function useMergeControlState() {
  const context = use(MergeControlStateContext)
  if (!context) throw new Error('Merge control components must be rendered within MergeControl')
  return context
}

export function useMergeControlActions() {
  const context = use(MergeControlActionsContext)
  if (!context) throw new Error('Merge control components must be rendered within MergeControl')
  return context
}

export function useMergeControlInputs() {
  const context = use(MergeControlInputsContext)
  if (!context) throw new Error('Merge control components must be rendered within MergeControl')
  return context
}
